import { API, graphqlOperation } from 'aws-amplify'
import { getUser } from '@/api/queries'
import store from '@/store/index.js'

export async function isAllowedByRoute(userId, group, route) {

    if(!route) {
        throw "route variable cannot be empty"
    }

    let userPermissions = await getUserPermissions(userId, group)
    let isAllowed = Object.keys(userPermissions)

    if(!userPermissions.permissionLogin) {
        throw "permissionLogin is false"
    }

    // array of tuples
    let mapUrlToPermissions = [
        ['/settings/account-details', 'permissionFullAccess'],
        ['/settings/invoices', 'permissionFullAccess'],
        ['/settings/company', 'permissionFullAccess'],
        ['/settings/users', 'permissionFullAccess'],
        ['/settings/driver-feedback', 'permissionFullAccess'],
        ['/settings/hera-ai', 'permissionFullAccess'],
        ['/settings/messages', 'permissionFullAccess'],
        ['/settings/messenger', 'permissionMessenger'],
        ['/settings/drop-downs', 'permissionCustomLists'],
        ['/settings/custom-lists', 'permissionCustomLists'],
        ['/settings/vehicle-photo-logs', 'permissionVehiclePhotoLogs'],
        ['/settings/labels', 'permissionManageLabels'],
        ['/settings/roster-checklists', 'permissionCompanySettingsRosterChecklistTemplates'],
        ['/settings/associate-app', 'permissionFullAccess'],
        ['/settings/file-management', 'permissionFullAccess']
    ]

    let isRouteProtectedByPermission = mapUrlToPermissions.some(permission => permission[0] == route)

    if(isRouteProtectedByPermission) {
        if(isAllowed.includes('permissionFullAccess')) {
            return true
        }

        let permissionRequiredByRoute = mapUrlToPermissions
        .filter(permission => permission[0] == route)
        .map(userPermission => userPermission[1])

        return isAllowed.includes(permissionRequiredByRoute[0])
    }

    return true

}

export function getTenantFeaturePermissions(){
    let tenantData = store.state.userInfo.tenant
    let tenantPermissions = {};
    
    const permissionsConfig = {
        'VPLAi': {
            accessKey: 'featureAccessVehiclePhotoLogsHeraAi',
            enabledKey: 'featureEnabledVehiclePhotoLogsHeraAi',
            permissions: {
                'permissionHeraAi': 'permissionHeraAi',
                'permissionVehiclePhotoLogsHeraAi': 'permissionVehiclePhotoLogsHeraAi',
            }
        },
        'Inventory': {
            accessKey: 'featureAccessInventoryManagement',
            enabledKey: 'featureEnabledInventoryManagement',
            permissions: {
                'permissionInventoryManagement': 'permissionInventoryManagement',
            }
        },
        'SR': {
            accessKey: 'featureAccessSchedulingAndRostering',
            enabledKey: 'featureEnabledSchedulingAndRostering',
            permissions: {
                'permissionSchedulingAndRostering': 'permissionSchedulingAndRostering',
            }
        },
        'RCL': {
            accessKey: 'featureAccessRosterChecklist',
            enabledKey: 'featureEnabledRosterChecklist',
            permissions: {
                'permissionRosterChecklist': 'permissionRosterChecklist',
            }
        },
        // Wireless Support Integration
        'WSI': {
            accessKey: 'featureAccessWirelessSupportSync',
            enabledKey: 'featureEnabledWirelessSupportSync',
            permissions: {
                'permissionWirelessSupportSync': 'permissionWirelessSupportSync',
            }
        },
        // Associate App
        'AA': {
            accessKey: 'featureAccessAssociateApp',
            enabledKey: 'featureEnabledAssociateApp',
            permissions: {
                'permissionAssociateApp': 'permissionAssociateApp',
            }
        },
        'HeraMessenger2': {
            accessKey: 'featureAccessHeraMessenger2',
            enabledKey: 'featureEnabledHeraMessenger2',
            permissions: {
                'permissionHeraMessenger2': 'permissionHeraMessenger2'
            }
        },
        'PhoneAutomation': {
            accessKey: 'featureAccessPhoneAutomation',
            enabledKey: 'featureEnabledPhoneAutomation',
            permissions: {
                'permissionPhoneAutomation': 'permissionPhoneAutomation'
            }
        },
        // Files Management
        'FM': {
            accessKey: 'featureAccessFilesManagement',
            enabledKey: 'featureEnabledFilesManagement',
            permissions: {}
        },
        // Files Signature Requests
        'FSR': {
            accessKey: 'featureAccessFilesSignatureRequest',
            enabledKey: 'featureEnabledFilesSignatureRequest',
            permissions: {}
        },
        'HP': {
            accessKey: 'featureAccessHierarchicalPermissions',
            enabledKey: 'featureEnabledHierarchicalPermissions',
            permissions: {
                'permissionHierarchicalPermissions': 'permissionHierarchicalPermissions'
            }
        },
        'AIChat': {
            accessKey: 'featureAccessAIChat',
            enabledKey: 'featureEnabledAIChat',
            permissions: {
                'permissionAIChat': 'permissionAIChat'
            }
        },
        'AmazonChromePlugin': {
            accessKey: 'featureAccessAmazonChromePlugin',
            enabledKey: 'featureEnabledAmazonChromePlugin',
            permissions: {
                'permissionAmazonChromePlugin': 'permissionAmazonChromePlugin'
            }
        },
        // Payroll Provider
        'PP': {
            accessKey: 'featureAccessPayrollProvider',
            enabledKey: 'featureEnabledPayrollProvider',
            permissions: {
                'permissionPayrollProvider': 'permissionPayrollProvider'
            }
        },
        // Vehicle Photo Log Review (HERA-9173). User permission is HERA-9174
        'VPLR': {
            accessKey: 'featureAccessVehiclePhotoLogReview',
            enabledKey: 'featureEnabledVehiclePhotoLogReview',
            permissions: {}
        },
    };
    
    Object.keys(permissionsConfig).forEach(epic => {
        const config = permissionsConfig[epic];
        if (!config.accessKey) return false
        const access = tenantData[config.accessKey] || false;
        
        tenantPermissions[config.accessKey] = access;
        tenantPermissions[config.enabledKey] = access ? tenantData[config.enabledKey] : false;
        
        for (const [key, value] of Object.entries(config.permissions)) {
            tenantPermissions[key] = access && tenantPermissions[config.enabledKey] ? tenantData[value] : false;
        }
    });
    return tenantPermissions
}

export async function getUserPermissions(userId, group) {

    let user = await API.graphql(graphqlOperation(getUser, {id: userId, group: group}));

    let userData = user.data.getUser

    // array of tuples
    let userPermissions = [
        ['permissionLogin', userData.permissionLogin],
        ['permissionFullAccess', userData.permissionFullAccess],
        ['permissionDocuments', userData.permissionDocuments],
        ['permissionManageCounselings', userData.permissionManageCounselings],
        ['permissionDeleteCounselings', userData.permissionDeleteCounselings],
        ['permissionAccidents', userData.permissionAccidents],
        ['permissionInjuries', userData.permissionInjuries],
        ['permissionDrugTests', userData.permissionDrugTests],
        ['permissionMessenger', userData.permissionMessenger],
        ['permissionPerformanceCoaching', userData.permissionPerformanceCoaching],
        ['permissionDAManagement', userData.permissionDAManagement],
        ['permissionCustomLists', userData.permissionCustomLists],
        ['permissionVehiclePhotoLogs', userData.permissionVehiclePhotoLogs],
        ['permissionVehiclePhotoLogReview', userData.permissionVehiclePhotoLogReview],
        ['permissionManageLabels', userData.permissionManageLabels],
        ['permissionVehicleManagement', userData.permissionVehicleManagement],
        ['permissionTasksReports', userData.permissionTasksReports],
        ['permissionCompanySettingsRosterChecklistTemplates', userData.permissionCompanySettingsRosterChecklistTemplates],
        ['permissionCompanySettingsSchedulingRostering', userData.permissionCompanySettingsSchedulingRostering],
        ['permissionMessageTemplate', userData.permissionMessageTemplate],
        ['permissionVehicleImages', userData.permissionVehicleImages],
        ['permissionVehicleRelatedDocuments', userData.permissionVehicleRelatedDocuments],
        ['permissionOtherFiles', userData.permissionOtherFiles],
        ['permissionCompanyDevices', userData.permissionCompanyDevices],
        ['permissionLocationDataFromCompanyDevices', userData.permissionLocationDataFromCompanyDevices],
        ['permissionAIChat', userData.permissionAIChat]
    ]

    let isAllowed = {}

    if(userData.permissionFullAccess) {
        for (const key of userPermissions) {
            isAllowed[key[0]] = true;
        }
        isAllowed.permissionLogin = userData.permissionLogin
        // S&R permission is gated by unreleased feature flag — do not auto-grant to Full Admins
        isAllowed.permissionCompanySettingsSchedulingRostering = userData.permissionCompanySettingsSchedulingRostering || false
        return isAllowed
    }

    let userPer = userPermissions
    .filter(userPermission => userPermission[1] == true)
    for (const key of userPer) {
        isAllowed[key[0]] = true;
    }

    return isAllowed
}

export async function getRoute(userId, group){
    /**
     * Make Component SettingsNoSelection the default view
     */
    let route = 'SettingsNoSelection'

    return route
}